import { AppBar, Box, Card, CardContent, FormControl, FormControlLabel, FormLabel, Grid, Switch, Toolbar, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { Constants } from "assets/appConfig";
import { FormStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonSave from "components/Buttons/ButtonSave";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import IconTrophy from "components/Icons/IconTrophy";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { RecordAbout } from "components/RecordAbout";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldNumeric from "components/TextFieldNumeric";
import { TextPluralization } from "components/TextPluralization";
import LibUtils from "lib/LibUtils";
import { GetAwardDto, SaveAwardDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { Prompt } from "react-router";
import AwardTopicDialog from "views/Gamification/Components/AwardTopicDialog";
import AwardLogDialog from "views/Gamification/Dialogs/AwardLogDialog";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
}

interface IState {
    dialog: any,
    lastError: any;
    formCycle: RequestLifeCycle,
    record: Partial<GetAwardDto>,
    validate: {
        grantPostPoints: boolean;
        grantQuestionPoints: boolean;
        grantProfileCredentialPoints: boolean;
        quizNewSessionMaxPoints: boolean;
        quizRevisionMaxPoints: boolean;
        courseCompletePoints: boolean;
    },
    updateHistory: {
        postPoints: boolean;
        questionPoints: boolean;
    },
}

type Props = IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class AwardEdit extends React.Component<Props, IState> {

    private compareDto: any = null;

    ///
    constructor(props: Props) {
        super(props);

        this.compareDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            dialog: null,
            record: {
                tenantId: Constants.EmptyGuid,
                grantProfileCredentialPoints: null,
                grantPostPoints: 1,
                grantQuestionPoints: 2,
                quizNewSessionMaxPoints: 20,
                quizRevisionMaxPoints: 10,
                courseCompletePoints: 10,
                counters: {
                    logEntries: 0,
                    topicExceptions: 0
                }
            },
            validate: {
                grantPostPoints: true,
                grantProfileCredentialPoints: true,
                grantQuestionPoints: true,
                quizNewSessionMaxPoints: true,
                quizRevisionMaxPoints: true,
                courseCompletePoints: true,
            },
            updateHistory: {
                postPoints: false,
                questionPoints: false,
            },
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Gamification.Components.AwardEdit." + r, f);
    }

    ///
    private load() {
        api.getAwardRecord()
            .then((dto) => {

                if (dto != null) {
                    this.compareDto = LibUtils.deepCopy(dto);
                    this.setState({
                        formCycle: RequestLifeCycle.InitSuccess,
                        record: dto,
                        validate: {
                            grantPostPoints: true,
                            grantProfileCredentialPoints: true,
                            grantQuestionPoints: true,
                            quizNewSessionMaxPoints: true,
                            quizRevisionMaxPoints: true,
                            courseCompletePoints: true
                        }
                    });
                }
                else {
                    this.compareDto = LibUtils.deepCopy(this.state.record);
                    this.setState({
                        formCycle: RequestLifeCycle.InitSuccess,
                    });
                }
            })
            .catch((err: any) => {
                this.setState({
                    lastError: err,
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private getPostDto(): SaveAwardDto {
        const { record } = this.state;

        return {
            courseCompletePoints: record.courseCompletePoints,
            grantPostPoints: record.grantPostPoints,
            grantProfileCredentialPoints: record.grantProfileCredentialPoints,
            grantQuestionPoints: record.grantQuestionPoints,
            quizNewSessionMaxPoints: record.quizNewSessionMaxPoints,
            quizRevisionMaxPoints: record.quizRevisionMaxPoints,
            updateHistoryPostPoints: this.state.updateHistory.postPoints,
            updateHistoryQuestionPoints: this.state.updateHistory.questionPoints
        };
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        this.setState(state => {
            switch (name) {
                case "grantPost":
                    state.record.grantPostPoints = v == false ? null : this.state.record.grantPostPoints == null ? 1 : this.state.record.grantPostPoints;
                    state.validate.grantPostPoints = true;
                    break;
                case "grantPostPoints":
                    state.record.grantPostPoints = v == "" ? null : v;
                    state.validate.grantPostPoints = v == "" || v > 0;
                    break;
                case "grantQuestion":
                    state.record.grantQuestionPoints = v == false ? null : this.state.record.grantQuestionPoints == null ? 1 : this.state.record.grantQuestionPoints;
                    state.validate.grantQuestionPoints = true;
                    break;
                case "grantQuestionPoints":
                    state.record.grantQuestionPoints = v == "" ? null : v;
                    state.validate.grantQuestionPoints = v == "" || v > 0;
                    break;
                case "grantProfileCredential":
                    state.record.grantProfileCredentialPoints = v == false ? null : this.state.record.grantProfileCredentialPoints == null ? 1 : this.state.record.grantProfileCredentialPoints;
                    state.validate.grantProfileCredentialPoints = true;
                    break;
                case "grantProfileCredentialPoints":
                    state.record.grantProfileCredentialPoints = v == "" ? null : v;
                    state.validate.grantProfileCredentialPoints = v == "" || v > 0;
                    break;

                case "quizNewSessionMaxPoints":
                    state.record.quizNewSessionMaxPoints = v;
                    state.validate.quizNewSessionMaxPoints = v > 0;
                    break;
                case "quizRevisionMaxPoints":
                    state.record.quizRevisionMaxPoints = v;
                    state.validate.quizRevisionMaxPoints = v > 0;
                    break;

                case "courseCompletePoints":
                    state.record.courseCompletePoints = v;
                    state.validate.courseCompletePoints = v >= 0;
                    break;

                default:
                    state.record[name] = v;
                    break;
            }
            return state;
        });
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (!this.formIsValid()) return;

        const dto = this.getPostDto();

        this.setState({
            formCycle: RequestLifeCycle.Posting,
        });

        api.saveAwardRecord(dto)
            .then((saved) => {
                this.compareDto = LibUtils.deepCopy(this.state.record);
                this.props.snackbar.showMessage(this.res("UpdateSuccess"), "success");
                this.setState({ formCycle: RequestLifeCycle.PostSuccess });
            })
            .catch((err: any) => {
                this.setState({ lastError: err, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private isRecordChanged() {
        return this.state.lastError == null && !LibUtils.deepCompare(this.state.record, this.compareDto);
    }

    ///
    private formIsValid() {
        return LibUtils.isAllPropertiesWithValue(this.state.validate, true);
    }

    ///
    private handleTopicException(initialMode: "create" | "list") {
        this.setState({
            dialog:
                <AwardTopicDialog
                    initialMode={initialMode}
                    onClose={
                        (newTotal) => {
                            if (newTotal == null) {
                                this.setState({ dialog: null });
                            }
                            else {
                                if (initialMode == "create" && newTotal == 1)
                                    newTotal = this.state.record.counters.topicExceptions + 1;

                                this.setState({
                                    dialog: null,
                                    record: {
                                        ...this.state.record,
                                        ...{
                                            counters: {
                                                topicExceptions: newTotal,
                                                logEntries: this.state.record.counters.logEntries
                                            }
                                        }
                                    }
                                });
                            }
                        }
                    }
                />
        });
    }

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar}>
                <Toolbar>
                    <div className={GlobalClassKeys.pushRight}>
                        <ButtonSave
                            disabled={!this.formIsValid()}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                if (this.state.updateHistory.postPoints || this.state.updateHistory.questionPoints) {
                                    this.setState({
                                        dialog: <MuiConfirmation
                                            message={this.res("ConfirmationUpdatePoints")}
                                            onClose={
                                                (confirm) => {
                                                    if (confirm) this.setState({ dialog: null }, () => this.handleSubmit(e));
                                                    else this.setState({ dialog: null });
                                                }
                                            }
                                        />
                                    });
                                }
                                else {
                                    this.handleSubmit(e);
                                }
                            }}
                        />
                    </div>
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <RecordAbout
                        record={this.state.record.recordInfo}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <AwardLogDialog
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderFormAward() {
        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("AwardTitle")} />

                    <CardContent>

                        <Grid container justifyContent="space-between" alignContent="center" alignItems="center">
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="grantPost"
                                            checked={record.grantPostPoints != null}
                                            onChange={this.handleChange}
                                            value="grantPost"
                                            color="secondary"
                                        />
                                    }
                                    label={this.res("GrantPostLabel")}
                                    labelPlacement="end"
                                />
                            </Grid>
                            <Grid item>
                                <FormControl fullWidth margin="dense">
                                    <TextFieldNumeric
                                        name="grantPostPoints"
                                        disabled={record.grantPostPoints == null}
                                        allowNegative={false}
                                        error={validate.grantPostPoints === false}
                                        margin="dense"
                                        value={record.grantPostPoints}
                                        onChange={this.handleChange}
                                        InputProps={
                                            {
                                                startAdornment: <IconTrophy fixedWidth />
                                            }
                                        }
                                        fullWidth
                                        required
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <FormControlLabel
                            control={
                                <Switch
                                    name="updateHistoryPostPoints"
                                    checked={this.state.updateHistory.postPoints}
                                    onChange={(e) => this.setState({ updateHistory: { ...this.state.updateHistory, postPoints: e.target.checked }})}
                                    color="secondary"
                                />
                            }
                            disabled={record.grantPostPoints == this.compareDto.grantPostPoints || record.grantPostPoints == null || record.grantPostPoints == 0}
                            label={this.res("UpdatePostPointsLabel")}
                            labelPlacement="end"
                        />

                        <Grid container justifyContent="space-between" alignContent="center" alignItems="center">
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="grantQuestion"
                                            checked={record.grantQuestionPoints != null}
                                            onChange={this.handleChange}
                                            value="isActive"
                                            color="secondary"
                                        />
                                    }
                                    label={this.res("GrantQuestionLabel")}
                                    labelPlacement="end"
                                />
                            </Grid>
                            <Grid item>
                                <FormControl fullWidth margin="dense">
                                    <TextFieldNumeric
                                        name="grantQuestionPoints"
                                        disabled={record.grantQuestionPoints == null}
                                        allowNegative={false}
                                        error={validate.grantQuestionPoints === false}
                                        margin="dense"
                                        value={record.grantQuestionPoints}
                                        onChange={this.handleChange}
                                        InputProps={
                                            {
                                                startAdornment: <IconTrophy fixedWidth />
                                            }
                                        }
                                        fullWidth
                                        required
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <FormControlLabel
                            control={
                                <Switch
                                    name="updateHistoryQuestionPoints"
                                    checked={this.state.updateHistory.questionPoints}
                                    disabled={record.grantQuestionPoints == this.compareDto.grantQuestionPoints || record.grantQuestionPoints == null || record.grantQuestionPoints == 0}
                                    onChange={(e) => this.setState({ updateHistory: { ...this.state.updateHistory, questionPoints: e.target.checked } })}
                                    color="secondary"
                                />
                            }
                            label={this.res("UpdateQuestionPointsLabel")}
                            labelPlacement="end"
                        />

                        <Grid container justifyContent="space-between" alignContent="center" alignItems="center">
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="grantProfileCredential"
                                            checked={record.grantProfileCredentialPoints != null}
                                            onChange={this.handleChange}
                                            value="isActive"
                                            color="secondary"
                                        />
                                    }
                                    label={this.res("GrantCredentialLabel")}
                                    labelPlacement="end"
                                />
                            </Grid>
                            <Grid item>
                                <FormControl fullWidth margin="dense">
                                    <TextFieldNumeric
                                        name="grantProfileCredentialPoints"
                                        disabled={record.grantProfileCredentialPoints == null}
                                        allowNegative={false}
                                        error={validate.grantProfileCredentialPoints === false}
                                        margin="dense"
                                        value={record.grantProfileCredentialPoints}
                                        onChange={this.handleChange}
                                        InputProps={
                                            {
                                                startAdornment: <IconTrophy fixedWidth />
                                            }
                                        }
                                        fullWidth
                                        required
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                    </CardContent>

                </Card>

            </MuiContainer >
        );
    }

    ///
    private renderFormTest() {

        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("TestSessionTitle")} />

                    <CardContent>

                        <FormControl margin="dense">
                            <FormLabel htmlFor="quizNewSessionMaxPoints">{this.res("GrantNewSessionLabel")}</FormLabel>
                            <TextFieldNumeric
                                name="quizNewSessionMaxPoints"
                                allowNegative={false}
                                error={validate.quizNewSessionMaxPoints === false}
                                margin="dense"
                                value={record.quizNewSessionMaxPoints}
                                onChange={this.handleChange}
                                InputProps={
                                    {
                                        startAdornment: <IconTrophy fixedWidth />
                                    }
                                }
                                required
                            />
                        </FormControl>

                        <br />
                        <br />

                        <FormControl margin="dense">
                            <FormLabel htmlFor="quizRevisionMaxPoints">
                                {this.res("GrantSessionRevisionLabel")}
                                <Typography variant="subtitle2">{this.res("GrantSessionRevisionHelper")}</Typography>
                            </FormLabel>
                            <TextFieldNumeric
                                name="quizRevisionMaxPoints"
                                allowNegative={false}
                                error={validate.quizRevisionMaxPoints === false}
                                margin="dense"
                                value={record.quizRevisionMaxPoints}
                                onChange={this.handleChange}
                                InputProps={
                                    {
                                        startAdornment: <IconTrophy fixedWidth />
                                    }
                                }
                                required
                            />
                        </FormControl>

                        {
                            this.state.record.tenantId != Constants.EmptyGuid &&
                            <>
                                <br />

                                <Grid container justifyContent="center" alignItems="center" alignContent="center">
                                    <Grid item>
                                        <IconButtonAdd
                                            color="warning"
                                            onClick={() => { this.handleTopicException("create"); }}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        {
                                            record.counters.topicExceptions > 0
                                                ? (
                                                    <>
                                                        <br />
                                                        <MuiLink
                                                            color="secondary"
                                                            onClick={() => { this.handleTopicException("list"); }}
                                                        >
                                                            <Typography color="inherit" variant="button">{this.res("ExceptionsMessage")}</Typography>
                                                        </MuiLink>
                                                        <Typography variant="subtitle2">
                                                            <MuiMuted>
                                                                <TextPluralization count={record.counters.topicExceptions} resource="Views.Gamification.Components.AwardEdit.ExceptionText" />
                                                            </MuiMuted>
                                                        </Typography>
                                                    </>
                                                )
                                                : <Typography variant="body1">{this.res("ExceptionsMessage")}</Typography>
                                        }
                                    </Grid>
                                </Grid>

                            </>
                        }

                    </CardContent>
                </Card>

            </MuiContainer >
        );
    }

    ///
    private renderFormCourse() {

        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("CourseTitle")} />

                    <CardContent>
                        <FormControl margin="dense">
                            <FormLabel htmlFor="courseCompletePoints">{this.res("GrantCourseCompleteLabel")}</FormLabel>
                            <TextFieldNumeric
                                name="courseCompletePoints"
                                allowNegative={false}
                                error={validate.courseCompletePoints === false}
                                margin="dense"
                                value={record.courseCompletePoints}
                                onChange={this.handleChange}
                                InputProps={
                                    {
                                        startAdornment: <IconTrophy fixedWidth />
                                    }
                                }
                                required
                            />
                        </FormControl>

                        <br />
                        <br />

                        <MuiMessageBox
                            size="normal"
                            variant="warning"
                            message={this.res("CourseAwardsHelperMessage")}
                        />
                    </CardContent>

                </Card>

            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.load();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    public render() {
        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }

        if (this.state.formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport error={this.state.lastError} />;
        }

        return (
            <Grid container justifyContent="space-around">
                <Grid item xs>
                    <div className={this.props.classes.formContainer}>

                        {this.state.dialog}

                        {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                        <RequestErrorDialog
                            open={this.state.formCycle == RequestLifeCycle.PostFailed}
                            error={this.state.lastError}
                            onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                        />

                        {this.renderToolbar()}

                        <form noValidate autoComplete="off" >
                            {this.renderFormAward()}
                            {this.renderFormTest()}
                            {this.renderFormCourse()}
                            {this.renderRecordInfo()}
                        </form>

                        <Prompt
                            when={this.isRecordChanged()}
                            message={this.props.t("Common.Question.Unsave")}
                        />
                    </div>
                </Grid>
            </Grid>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(AwardEdit)));
